export const blog4 = `

<div style="text-align: justify;">

In this white paper we shortly describe the current landscape of type 1 diabetes management and 
why open-source looping technology is essential for the future of diabetes care. 

Over the past five decades, there has been a notable surge in its incidence globally, 
with current statistics revealing its widespread impact. On this moment 6 in 10,000 people have
 type 1 diabetes and every year 1.5 out of 10,000 people get type 1 diabetes.
<br/>
<div>
<div style="margin-bottom:-20px">
<caption><h4>Prevalence and incidence of type 1 diabetes in the world</h4></caption>
</div>
    <table>
        <tr>
            <th style="width: 100px; text-align: left;">Region</th>
            <th style="width: 200px; text-align: center;">Prevalence Per 10 000</th>
            <th style="width: 200px; text-align: center;">Incidence Per 100 000</th>
            <th></th>
            <th></th>
            <th></th>
        </tr>
        <tr>
            <td style="text-align: left;">World</td>
            <td style="text-align: center;">5.9</td>
            <td style="text-align: center;">15</td>
            <td></td>
            <td></td>
            <td></td>
        </tr>
        <tr>
            <td style="text-align: left;">Asia</td>
            <td style="text-align: center;">9.6</td>
            <td style="text-align: center;">15</td>
            <td></td>
            <td></td>
            <td></td>
        </tr>
        <tr>
            <td style="text-align: left;">Africa</td>
            <td style="text-align: center;">5.3</td>
            <td style="text-align: center;">8</td>
            <td></td>
            <td></td>
            <td></td>
        </tr>
        <tr>
            <td style="text-align: left;">Europe</td>
            <td style="text-align: center;">2.12</td>
            <td style="text-align: center;"></td>
            <td></td>
            <td></td>
            <td></td>
        </tr>
        <tr>
            <td style="text-align: left;">America</td>
            <td style="text-align: center;">3.9</td>
            <td style="text-align: center;">20</td>
            <td></td>
            <td></td>
            <td></td>
        </tr>
    </table>
</div>


[Source: Prevalence and incidence of type 1 diabetes in the world: a systematic review and meta-analysis
 Mobasseri et al. ](https://www.ncbi.nlm.nih.gov/pmc/articles/PMC7146037/)


Despite ongoing research into the causes and potential cures, 
access to advanced diabetes technology remains pivotal in enhancing 
disease management and quality of life. This white paper emphasizes the necessity for 
universally accessible looping technology for diabetes, 
the essential characteristics such technology should embody, and the collaborative efforts required to 
achieve these goals through a medical Open-source software framework that we aim to provide via Opensource.clinic.

Our mission is based on three core principles:

1.  **Accessibility**: Every person with type 1 diabetes, or their caregiver, should be informed about and have access to advanced diabetes technologies.

2.  **Usability**: The technology should be intuitive, user-friendly, and provide the necessary support in order to be used by all demographic groups (exceptions to this are discussed later).

3.  **Collaboration**: To achieve the above two goals, 1) the medical community, 2) IT experts, and 3) persons with diabetes (PWD) or their caregivers must work together in a medical open-source software triangle that improves physical and mental health, safety and data security.

<br/>

###  1. Accessibility of Advanced Health Technology

#### Barriers of current advanced diabetes technology

Advanced health technology for diabetes includes continuous glucose monitors (CGMs), insulin pumps, mobile health applications, 
and telemedicine services. 
The devices and apps have been connected to create automated insuline delivery systems, so-called closed-loop and hybrid closed loop systems. 
These technologies have been shown to significantly enhancing disease management and patient outcomes. 

The commercial open-loop systems that are currently available however face several barriers:

-   **Economical**: Due to high costs the commercial looping systems are only available to bad-regulated PWD. A commercial looping system can cost from 5,000-7,000 euro's, this excludes the training and support given by health care providers.

-   **Geographical**: Commercial looping systems, whether it be the software or the hardware compatible with the software, are often too expensive for low to middle-income countries.

-   **Customizability**: The algorithms are proprietary, creating a "black box" problem for health providers and PWD meaning we can not inspect the inner workings or fine-tune the systems according to the needs of PWD.

#### Advantages and disadvantages of Open Source diabetes technology

Open source solutions do not face these barriers and are more advanced in respect to certain aspects. The #WeAreNotWaiting community pioneered open-source looping and cloud monitoring over 15 years ago. In more recent years the community has been able to integrate smartwatches, activity trackers, and AI assistants in diabetes management. Open source systems are at in many aspects at frontier of technological development and also not susceptible to the economic and geographical barriers mentioned above. 

Open-source looping systems however have their own challenges: 
-   **Educational**: Low sense of self-determinism and/or education levels can create mental resistance against the complexities of setting up an open-source looping system.

-   **Awareness**: Many are unaware of the existense of open-source solutions or unaware of the fact that these systems live up or exceed the performance of commercial systems.

-   **Safety Concerns**: PWD can worry about the safety of using medical devices off-label and/or the implementation of uncertified software for their own health or that of their beloved ones.

-   **Legal Considerations**: Health providers are cautious about recommending it due to liability concerns. Who is going to be responsible if something goes wrong due to an unforeseen bug in the software?

We believe that the most problematic for the integration of open source solutions in diabetes care is the educational barrier. 
If this barrier is removed, the other barriers can be addressed more easily.
The primary goal is therefore to make open-source looping technology accessible to all PWD, regardless of their technical expertise and educational level. 


<br/>

### 2. Usability of Open Source Looping Systems

To tackle the most important adoption barriers, the following steps are necessary:

For Medical Professionals:
-   Enhanced education and training for diabetes nurses, internal medicine doctors, and general practitioners.
-   Emphasis on safety through highlighting published scientific research of open source systems.
-   Address legal concerns.
-   Facilitate research with data provided open source loopers (also see <a href="https://www.openhumans.org/" target="_blank" rel="noopener noreferrer">Open Humans</a>)

For PWD:
-   Improved setup and onboarding processes.
-   Easy-to-use software.
-   Increased awareness through social media and other channels.
-   Provide 24/7 helpdesk support and monitoring.
-   Comprehensive and well-written/recorded tutorials.


The aim of Opensource.clinic is to tackle the points above by providing a comprehensive and easy-to-understand educational platform that will guide PWD through the setup and maintenance of open-source looping systems. 
This will be complemented by a 24/7 helpdesk and robust customer service to address any concerns or issues that may arise.

<br/>

### 3. Medical Open-source software Triangle

To realize the goals outlined in sections 1 and 2, a collaborative framework involving three key stakeholders is essential: Medical Community and Health Professionals, IT specialists and Users (PWD and Caregivers).

<br/>


#### Medical Community and Health Professionals

-   Clinical Input: Provide insights into medically sound decision making, highlight important safety aspects and emphasize clinical needs.

-   Validation and Testing: Participate in rigorous evaluation and testing to ensure technologies meet medical standards.

#### IT Experts

-   Development and Maintenance: Design, develop, and maintain the software and hardware components.

-   Security and Privacy: Implement robust data security measures to protect patient privacy.

-   Data Privacy: Ensure compliance with regulations like GDPR and HIPAA to safeguard patient information.

#### Users

-   Feedback and Testing: Offer continuous feedback to refine and improve technology.

-   Advocacy and Outreach: Advocate for the adoption and dissemination of advanced technologies.

-   Health Improvements: Focus on continuous improvement of technology to enhance health outcomes and quality of life for PWD.


</br></br>  
The current political medical landscape has created a stalemate for the development of accessible advanced diabetes technology. 
The adoption of an open source mindset to diabetes technologies can break this stalemate and has the potential to provide a solution that is accessible.
The #WeAreNotWaiting community has taken the lead in setting up succesfull looping systems based on the contibution of a community of software developers. 
These open source solutions can be made more widespread if the educational barrier is removed and the technology is made user-friendly and intuitive and as a consequence also safe.
By doing so, we blieve it's possible to revolutionize diabetes care and improve the quality of life for millions of PWD worldwide.


</div>

<!-- blog-contact-footer -->
`;
